import React, { useState } from 'react';
import { InputField } from 'components/FormFields/InputField';
import { HideEye } from 'components/AccountLoginRegistration/Icons/HideEye';
import { ShowEye } from 'components/AccountLoginRegistration/Icons/ShowEye';
import { ValidationTooltip } from './ValidationTooltip';
import styles from './styles.module.scss';

/**
 * PasswordField component renders a password input with visibility toggle and optional validation tooltip.
 *
 * @param {object} props - Component props.
 * @param {boolean} [props.createPassword=false] - Whether to show the validation tooltip for creating a password.
 * @param {object} props.form - The form object, from react-hook-form.
 * @returns {JSX.Element} The rendered PasswordField component.
 */
const PasswordField = ({
  createPassword = false,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  /**
   * Handles the focus event for the password input.
   * Used with validation tooltip to show when the input is focused.
   */
  const focusCallback = () => {
    setIsFocused(true);
  };

  /**
   * Handles the blur event for the password input.
   * Used with validation tooltip to hide when the input loses focus.
   */
  const blurCallback = () => {
    setIsFocused(false);
  };

  return (
    <div className={styles.passwordField} data-testid="password-field">
      <InputField
        type={showPassword ? 'text' : 'password'}
        aria-label="password"
        onFocus={createPassword ? focusCallback : null}
        onBlur={createPassword ? blurCallback : null}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />

      <button
        type="button"
        data-testid="toggle-password-visibility"
        aria-label="toggle password visibility"
        onClick={() => setShowPassword(!showPassword)}
        className={styles.eye}
      >
        {showPassword ? <HideEye /> : <ShowEye />}
      </button>

      {createPassword ? (
        <ValidationTooltip isFocused={isFocused} />
      ) : null }
    </div>
  );
};

export { PasswordField };
